import { CircleXIcon } from 'lucide-react';
import { type KeyboardEventHandler, type MouseEventHandler, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '~/hooks/use-debounce';
import { useSearchParams } from '~/hooks/use-search-params';
import { TableFilterBarContext } from '~/modules/common/data-table/table-filter-bar';
import { SearchSpinner } from '~/modules/common/search-spinner';
import { focusTask } from '~/modules/task/helpers/focus-task';
import { Badge } from '~/modules/ui/badge';
import { InputGroup, InputGroupAddon, InputGroupInput } from '~/modules/ui/input-group';
import { cn } from '~/utils/cn';

interface BoardSearchProps {
  children?: React.ReactNode;
  isInWorkspace?: boolean;
  toggleFocus: () => void;
}

export function BoardSearch({ children, isInWorkspace, toggleFocus }: BoardSearchProps) {
  const { t } = useTranslation();

  const inputRef = useRef<HTMLInputElement>(null);
  const { isFilterActive } = useContext(TableFilterBarContext);

  const {
    search: { q: searchQuery = '', matchMode = 'all' },
    setSearch,
  } = useSearchParams<{ q?: string; matchMode?: 'all' | 'any' }>({});

  const [inputValue, setInputValue] = useState(searchQuery);

  const debouncedQuery = useDebounce(inputValue, 250);

  const handleClick: MouseEventHandler = (e) => {
    const target = e.target as HTMLElement; // Cast the target to HTMLElement
    if (target.id === 'search-close' && document.activeElement !== inputRef.current) inputRef.current?.focus();

    focusTask(null);
  };

  const preventInputBlur: MouseEventHandler = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const handleKeyDown: KeyboardEventHandler = (e) => {
    if (e.key !== 'Escape') return;

    if (inputValue) {
      setInputValue('');
      setSearch({ q: '' });
    } else inputRef.current?.blur();
  };

  const toggleSearchMode = () => setSearch({ matchMode: matchMode === 'all' ? 'any' : 'all' });

  // Update parent query only when debouncedQuery changes
  useEffect(() => {
    if (debouncedQuery !== searchQuery) setSearch({ q: debouncedQuery });
  }, [debouncedQuery]);

  // Focus input when filter button clicked (mobile)
  useEffect(() => {
    if (isFilterActive) inputRef.current?.focus();
  }, [isFilterActive]);

  // Keep input value in sync with searchQuery when it changes externally
  useEffect(() => {
    if (searchQuery !== inputValue) setInputValue(searchQuery);
  }, [searchQuery]);

  const searchLabel = t('c:search_in_resource', {
    resource: isInWorkspace ? t('c:your_resource', { resource: t('c:workspace').toLowerCase() }) : t('c:project').toLowerCase(),
  });

  return (
    <InputGroup className="relative flex w-full items-center border-none shadow-none sm:min-w-44" onClick={handleClick} onKeyDown={handleKeyDown}>
      <InputGroupAddon className="pl-1.5">
        <SearchSpinner value={inputValue} isSearching={false} />
      </InputGroupAddon>

      <InputGroupInput
        ref={inputRef}
        name="boardSearch"
        onFocus={toggleFocus}
        onBlur={toggleFocus}
        placeholder={searchLabel}
        aria-label={searchLabel}
        className={'h-10 w-full border-0 pl-0! shadow-none'}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
      />

      <InputGroupAddon className="pr-2" align="inline-end">
        {children}
      </InputGroupAddon>
      <InputGroupAddon className="pr-2" align="inline-end">
        <button
          type="button"
          className={cn((!inputValue || !/\s/.test(inputValue)) && 'hidden', 'focus-effect rounded-full')}
          onMouseDown={preventInputBlur}
          onClick={toggleSearchMode}
        >
          <Badge variant="plain" size="micro" className="cursor-pointer">
            {matchMode}
          </Badge>
        </button>
      </InputGroupAddon>
      <InputGroupAddon className="pr-2" align="inline-end">
        <button
          type="button"
          id="search-close"
          aria-label={t('c:clear')}
          className={cn(!inputValue && 'hidden', 'focus-effect cursor-pointer rounded-full')}
          onMouseDown={preventInputBlur}
          onClick={() => {
            setSearch({ q: '', matchMode: undefined });
            setInputValue('');
            inputRef.current?.focus();
          }}
        >
          <CircleXIcon />
        </button>
      </InputGroupAddon>
    </InputGroup>
  );
}
